/** What the Rooms list reads and does, for the computer's page and Season's: the rooms, the quick lives, the Now band, the held strip, the filter. */
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { WebinarListResponse, WebinarSummary } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { flashsuitePages } from "../../components/FlashsuiteMenu";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { inApp, useShownAgain } from "../../lib/inApp";
import { lastLoaded, load, warm } from "../../lib/kept";
import { nowBandOf, quickLivesOf, roomCardsOf } from "./quickWords";
import { filterRooms, type RoomFilter } from "./roomWords";

export function prefetchRooms(): void {
  warm("webinars", () => api.webinars.list());
}

type Phase = "loading" | "ready" | "error";

function useRooms(): { data: WebinarListResponse | null; phase: Phase; reload: () => void } {
  const [data, setData] = useState<WebinarListResponse | null>(() => lastLoaded("webinars"));
  const [phase, setPhase] = useState<Phase>(() => (lastLoaded("webinars") ? "ready" : "loading"));
  const [nonce, setNonce] = useState(0);
  const shownAgain = useShownAgain();
  useEffect(() => {
    let alive = true;
    load("webinars", () => api.webinars.list())
      .then((d) => {
        if (!alive) return;
        setData(d);
        setPhase("ready");
      })
      .catch(() => {
        if (alive) setPhase("error");
      });
    return () => {
      alive = false;
    };
  }, [nonce, shownAgain]);
  return { data, phase, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

export function useRoomsPage() {
  const { user, flashsuite } = useAuth();
  const { data, phase, reload } = useRooms();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [filter, setFilter] = useState<RoomFilter>("all");
  // A failed payment or a full library holds new rooms and lives; the server refuses them too.
  const [held, setHeld] = useState<"payment" | "library" | null>(null);
  const shownAgain = useShownAgain();
  useEffect(() => {
    api.billing.alerts().then(
      (a) => setHeld(a.alert),
      () => setHeld(null),
    );
  }, [shownAgain]);
  // Why not inside the phone app: nothing there leads to buying.
  const billing = inApp() ? undefined : flashsuitePages(flashsuite)?.billing;

  // A room deleted from its own page comes back here with its toast once.
  useEffect(() => {
    const state = location.state as { toast?: string } | null;
    if (!state?.toast) return;
    toast(state.toast);
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate, toast]);

  // Why three lists: a quick live is a tile in its strip and a circle's call shows only while it is live.
  const everything: WebinarSummary[] = data?.webinars ?? [];
  const rooms = roomCardsOf(everything);
  const quick = quickLivesOf(everything);
  const band = nowBandOf(everything, Date.now());
  const counts: Record<RoomFilter, number> = {
    all: rooms.length,
    published: filterRooms(rooms, "published").length,
    drafts: filterRooms(rooms, "drafts").length,
  };
  return { hostFirst: user?.first_name ?? "", data, phase, reload, rooms, quick, band, counts, shown: filterRooms(rooms, filter), filter, setFilter, held, billing };
}
